import {
  AHSeparator,
  AHText,
  AHTodoCategoryIcon,
  Box,
  useLayoutContext,
} from "arkh";
import { ScrollView } from "react-native";
import { FormattedMessage, useIntl } from "react-intl";

import React, { useCallback } from "react";

import { StoreItem } from "./StoreItem";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import {
  useHasFeatureFlag,
  useIsStoresprintAdmin,
} from "../../useHasFeatureFlag";
import { FeatureFlags } from "../../config";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { StoreAdminScreenQuery } from "./__generated__/StoreAdminScreenQuery.graphql";
import { Features, useHasFeatureInTier } from "../../useHasFeatureInTier";
import { StoreAdminScreenUsersItem_Store$key } from "./__generated__/StoreAdminScreenUsersItem_Store.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { useNewFetchKeyOnNav } from "../../useNewFetchKeyOnNav";
import { StoreAdminNavigationScreenProps } from "./StoreAdminNavigation";
import { EntityHeader } from "@/src/components/EntityHeader";

export const StoreAdminScreen = (
  props: StoreAdminNavigationScreenProps<"StoreAdminMain">
) => {
  const render = useCallback(
    (fetchKey: string) => (
      <StoreAdminScreenComponent {...props} fetchKey={fetchKey} />
    ),
    [props]
  );
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {render}
    </LazyQueryNetworkBoundrary>
  );
};

function StoreAdminScreenComponent(
  props: StoreAdminNavigationScreenProps<"StoreAdminMain"> & {
    fetchKey: string;
  }
) {
  const isStoresprintAdmin = useIsStoresprintAdmin();
  const intl_tokens = useIntlTokens();
  const fetchKey = useNewFetchKeyOnNav(props.fetchKey);
  const data = useLazyLoadQuery<StoreAdminScreenQuery>(
    graphql`
      query StoreAdminScreenQuery {
        store {
          id
          name
          myRole
          defaultTodoCategory {
            id
            title
            icon
          }
          ...StoreAdminScreenUsersItem_Store
          ...useHasFeatureInTier_Store
          allTeams(includeArchived: false) {
            id
          }
          dashboards {
            id
          }
          calendars {
            id
          }
          todoCategories {
            id
            title
          }
          topics {
            __typename
          }

          screenCount

          KPIS: routines(type: KPI) {
            id
          }
        }
      }
    `,
    {},
    { fetchPolicy: "store-and-network", fetchKey }
  );

  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const intl = useIntl();

  const hasOperationsPlatform = useHasFeatureInTier(
    Features.OPERATIONS_PLATFORM,
    data.store
  );

  const hasConnect = useHasFeatureInTier(Features.CONNECT, data.store);

  const showTopicAdmin =
    data.store.myRole === "ADMIN" &&
    data.store.topics &&
    data.store.topics.length > 0;

  type Args = {
    isFirst?: boolean;
    isLast?: boolean;
  };

  const sections = [
    {
      label: intl.formatMessage({
        defaultMessage: "General",
        description: "StoreAdmin:Section label general",
      }),
      buttons: [
        data.store.myRole === "ADMIN"
          ? (args: Args) => (
              <UsersItem
                {...args}
                store={data.store}
                onPress={() =>
                  props.navigation.navigateDeprecated("UserAdmin", {})
                }
              />
            )
          : null,
        hasOperationsPlatform && data.store.myRole === "ADMIN"
          ? (args: Args) => (
              <StoreItem
                {...args}
                label={intl_tokens.Teams}
                detail={data.store.allTeams.length + ""}
                icon="team"
                onPress={() => {
                  props.navigation.navigateDeprecated("TeamsAdmin", {});
                }}
              />
            )
          : null,
        showTopicAdmin
          ? (args: Args) => (
              <StoreItem
                {...args}
                label={intl_tokens.Topics}
                detail={(data.store.topics?.length ?? 0) + ""}
                icon="topic"
                onPress={() => {
                  props.navigation.navigateDeprecated("TopicsAdmin", {});
                }}
              />
            )
          : null,

        (args: Args) => (
          <StoreItem
            {...args}
            label={intl_tokens.Calendars}
            detail={data.store.calendars.length + ""}
            icon="calendar"
            onPress={() => {
              props.navigation.navigate("CalendarsAdmin");
            }}
          />
        ),

        (args: Args) => (
          <StoreItem
            {...args}
            onPress={() => {
              props.navigation.navigateDeprecated("IntegrationAdmin", {});
            }}
            label={intl_tokens.Integrations}
            icon="integration"
            detail={""}
          />
        ),
      ],
    },
    {
      label: "Storecast",
      buttons: [
        (args: Args) => (
          <StoreItem
            {...args}
            label={intl.formatMessage({
              defaultMessage: "Screens",
              description: "StoreAdmin:Item label screens",
            })}
            detail={data.store.screenCount + ""}
            icon="tv"
            onPress={() => {
              props.navigation.navigateDeprecated("ScreenList", {});
            }}
          />
        ),
        (args: Args) => (
          <StoreItem
            {...args}
            label={intl_tokens.Dashboards}
            detail={data.store.dashboards.length + ""}
            icon="dashboard"
            onPress={() => {
              props.navigation.navigateDeprecated("DashboardsAdmin", {});
            }}
          />
        ),
      ],
    },
    {
      label: "Operations",
      buttons: [
        hasOperationsPlatform && data.store.defaultTodoCategory
          ? (args: Args) => (
              <StoreItem
                {...args}
                customIcon={
                  data.store.defaultTodoCategory!.icon ? (
                    <AHTodoCategoryIcon
                      icon={data.store.defaultTodoCategory!.icon}
                    />
                  ) : null
                }
                label={data.store.defaultTodoCategory!.title}
                detail={null}
                onPress={() => {
                  if (data.store.defaultTodoCategory) {
                    props.navigation.navigateDeprecated("TodoCategoryEdit", {
                      id: data.store.defaultTodoCategory!.id,
                    });
                  }
                }}
              />
            )
          : null,
        hasOperationsPlatform
          ? (args: Args) => (
              <StoreItem
                {...args}
                onPress={() => {
                  props.navigation.navigateDeprecated(
                    "TodoCategoryAdminList",
                    {}
                  );
                }}
                label={intl_tokens.CustomLists}
                icon="todo-category"
                detail={data.store.todoCategories.length + ""}
              />
            )
          : null,
        hasOperationsPlatform
          ? (args: Args) => (
              <StoreItem
                {...args}
                onPress={() => {
                  props.navigation.navigateDeprecated("KPIAdmin", {});
                }}
                label={intl_tokens.KPIs}
                icon="KPI"
                detail={data.store.KPIS?.length + ""}
              />
            )
          : null,
        hasOperationsPlatform
          ? (args: Args) => (
              <StoreItem
                {...args}
                onPress={() => {
                  props.navigation.navigateDeprecated(
                    "TeamScheduleAdminSelectTeam",
                    {}
                  );
                }}
                label={intl_tokens.TeamSchedule}
                icon="team-schedule"
                detail={data.store.todoCategories.length + ""}
              />
            )
          : null,
      ],
    },
    {
      label: "",
      buttons: [
        hasConnect
          ? (args: Args) => (
              <StoreItem
                {...args}
                onPress={() => {
                  props.navigation.navigateDeprecated("ConnectAdmin", {});
                }}
                label={intl.formatMessage({
                  defaultMessage: "StoreSprint Connect",
                  description: "StoreAdmin:Item label connect",
                })}
                icon="connect"
                detail=""
              />
            )
          : null,
      ],
    },
    {
      label: "",
      buttons: [
        isStoresprintAdmin
          ? (args: Args) => (
              <StoreItem
                {...args}
                onPress={() => {
                  props.navigation.navigate("SuperAdminPanel", {
                    storeId: data.store.id,
                  });
                }}
                label={"Super Admin Panel"}
                icon="sun"
                detail=""
              />
            )
          : null,
      ],
    },
  ];
  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: insets.bottom + layout.space.medium,

        flexGrow: 1,
      }}
    >
      <EntityHeader>
        <EntityHeader.Icon icon="store" />
        <EntityHeader.Title>{data.store.name}</EntityHeader.Title>
        <EntityHeader.Button
          onPress={() => {
            props.navigation.navigateDeprecated("StoreEditName", {});
          }}
        >
          <FormattedMessage
            defaultMessage="Edit Name"
            description="StoreAdmin:Edit name button"
          />
        </EntityHeader.Button>
      </EntityHeader>

      <Box
        spaceVertical="large"
        spaceHorizontal="medium"
        color="surface-background-light"
      >
        {sections.map((section, sectionIndex) => {
          const buttons = section.buttons.filter((s) => s);
          if (buttons.length === 0) {
            return null;
          }
          return (
            <Box spaceBottom="large" key={sectionIndex}>
              <AHText spaceBottom="small" spaceLeft="xsmall" type="title-small">
                {section.label}
              </AHText>
              {buttons.map((button, index, arr) => (
                <React.Fragment key={index}>
                  {index > 0 ? <AHSeparator /> : null}
                  <Box key={index}>
                    {button!({
                      isFirst: index === 0,
                      isLast: index === arr.length - 1,
                    })}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          );
        })}
      </Box>
    </ScrollView>
  );
}

function UsersItem(props: {
  onPress: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  store: StoreAdminScreenUsersItem_Store$key;
}) {
  const intl_tokens = useIntlTokens();
  const { userCount } = useFragment(
    graphql`
      fragment StoreAdminScreenUsersItem_Store on Store {
        userCount
      }
    `,
    props.store
  );
  return (
    <StoreItem
      isFirst={props.isFirst}
      isLast={props.isLast}
      label={intl_tokens.UsersAndDevices}
      detail={userCount + ""}
      icon="user"
      onPress={props.onPress}
    />
  );
}
